import apiClient from './apiClient';
import { handleApiError, formatApiResponse } from './api';

/**
 * Servicio para gestión de órdenes/ventas del portal (Admin)
 * Las órdenes son creadas por clientes desde el portal y gestionadas desde el admin
 */
export const ordenesService = {
  // Obtener lista de ventas del portal
  getOrdenes: async (params = {}) => {
    try {
      const response = await apiClient.get('/sales', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener venta por ID
  getOrdenById: async (id) => {
    try {
      const response = await apiClient.get(`/sales/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear nueva orden
  createOrden: async (ordenData) => {
    try {
      const response = await apiClient.post('/sales', ordenData);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Aprobar orden (usa endpoint específico /approve)
  approveOrden: async (id, data = {}) => {
    try {
      const response = await apiClient.put(`/sales/${id}/approve`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Rechazar orden (actualiza status a REJECTED)
  rejectOrden: async (id, reason = '') => {
    try {
      const response = await apiClient.put(`/sales/${id}/status`, {
        status: 'REJECTED',
        reason,
        notes: reason
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Completar orden (actualiza status a CLOSED_WON)
  completeOrden: async (id) => {
    try {
      const response = await apiClient.put(`/sales/${id}/status`, {
        status: 'CLOSED_WON'
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Cancelar orden (actualiza status a CLOSED_LOST)
  cancelOrden: async (id, reason = '') => {
    try {
      const response = await apiClient.put(`/sales/${id}/status`, {
        status: 'CLOSED_LOST',
        reason,
        notes: reason
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Convertir cotización (ESTIMATE) a orden (ORDER)
  convertToOrder: async (estimateId) => {
    try {
      const response = await apiClient.post(`/sales/${estimateId}/convert-to-order`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Convertir orden a factura
  convertToInvoice: async (orderId, invoiceData = {}) => {
    try {
      const response = await apiClient.post(`/sales/${orderId}/generate-invoice`, invoiceData);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener órdenes pendientes
  getOrdenesPendientes: async (params = {}) => {
    try {
      const response = await apiClient.get('/sales', {
        params: { ...params, status: 'PENDING' }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener órdenes aprobadas
  getOrdenesAprobadas: async (params = {}) => {
    try {
      const response = await apiClient.get('/sales', {
        params: { ...params, status: 'APPROVED' }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener estadísticas de órdenes
  getEstadisticas: async (params = {}) => {
    try {
      const response = await apiClient.get('/sales/statistics', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar orden
  updateOrden: async (id, data) => {
    try {
      const response = await apiClient.put(`/sales/${id}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar estado de la orden
  updateOrdenStatus: async (id, status, notes = '') => {
    try {
      const response = await apiClient.put(`/sales/${id}/status`, {
        status,
        notes
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Liberar orden (cambiar a RELEASED)
  releaseOrden: async (id) => {
    try {
      const response = await apiClient.put(`/sales/${id}/status`, {
        status: 'RELEASED'
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Detener orden (cambiar a ON_HOLD)
  holdOrden: async (id, reason = '') => {
    try {
      const response = await apiClient.put(`/sales/${id}/status`, {
        status: 'ON_HOLD',
        notes: reason
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Eliminar orden
  deleteOrden: async (id) => {
    try {
      const response = await apiClient.delete(`/sales/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
,

  // ===== VALIDACIÓN DE PRODUCTOS EN PRE_ORDER =====

  // Actualizar estado de validación de un producto individual
  updateItemValidationStatus: async (ordenId, itemId, validationData) => {
    try {
      const response = await apiClient.put(
        `/sales/${ordenId}/items/${itemId}/validate`,
        validationData
      );
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Validar todos los items de una orden automáticamente contra el inventario
  validateAllItems: async (ordenId) => {
    try {
      const response = await apiClient.post(`/sales/${ordenId}/validate-all-items`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Arreglar items de órdenes antiguas (inicializar quantity_requested)
  fixLegacyItems: async (ordenId) => {
    try {
      const response = await apiClient.post(`/sales/${ordenId}/fix-legacy-items`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ===== CAMBIOS DE ESTADO PARA PRE_ORDER =====

  // Cambiar a PROCESSING (En Proceso)
  setProcessingOrden: async (id) => {
    try {
      const response = await apiClient.put(`/sales/${id}/status`, {
        status: 'PROCESSING'
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Cambiar a COMPLETED (Completada)
  completePreOrden: async (id) => {
    try {
      const response = await apiClient.put(`/sales/${id}/status`, {
        status: 'COMPLETED'
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Convertir PRE_ORDER a ORDER
  convertPreOrderToOrder: async (id) => {
    try {
      const response = await apiClient.post(`/sales/${id}/convert-to-order`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
};

/**
 * Utilidades para manejo de órdenes/ventas
 */
export const ordenUtils = {
  // Mapear estados a español
  getStatusLabel: (status) => {
    const statusMap = {
      'PENDING_APPROVAL': 'Pendiente Aprobación',
      'APPROVED': 'Aprobada',
      'CONVERTED': 'Convertida',
      'CLOSED_WON': 'Ganada',
      'REJECTED': 'Rechazada',
      'CLOSED_LOST': 'Perdida',
      'DRAFT': 'Borrador',
      'ESTIMATE': 'Estimado',
      'SENT': 'Enviada',
      'NEGOTIATION': 'Negociación'
    };
    return statusMap[status] || status;
  },

  // Obtener color por estado
  getStatusColor: (status) => {
    const colorMap = {
      'PENDING_APPROVAL': 'warning',
      'APPROVED': 'info',
      'CONVERTED': 'success',
      'CLOSED_WON': 'success',
      'REJECTED': 'danger',
      'CLOSED_LOST': 'secondary',
      'DRAFT': 'secondary',
      'ESTIMATE': 'info',
      'SENT': 'info',
      'NEGOTIATION': 'warning'
    };
    return colorMap[status] || 'default';
  },

  // Verificar si se puede aprobar
  canApprove: (order) => {
    return order && order.status === 'PENDING_APPROVAL';
  },

  // Verificar si se puede rechazar
  canReject: (order) => {
    return order && order.status === 'PENDING_APPROVAL';
  },

  // Verificar si se puede convertir a factura
  canConvertToInvoice: (order) => {
    return order && order.status === 'APPROVED';
  },

  // Verificar si se puede completar
  canComplete: (order) => {
    return order && (order.status === 'APPROVED' || order.status === 'PENDING_APPROVAL');
  },

  // Calcular total de la venta
  calculateTotal: (order) => {
    if (!order || !order.items) return 0;
    return order.items.reduce((sum, item) => sum + (item.subtotal || item.total || 0), 0);
  }
};

export default ordenesService;
